import { useEffect, useState } from "react";
import { Button, Icon, Modal, SkeletonText } from "@astrum/ui";
import { api } from "../lib/api.js";
import type { Changelog } from "../lib/changelog-parse.js";
import { APP_VERSION } from "../lib/error-report.js";
import { useWhatsNewDot } from "../lib/whats-new.js";
import "./about-cards.css";

/** Where "Get help" goes; unset means the link is not shown rather than pointing nowhere. */
const HELP_URL: string | undefined = import.meta.env.VITE_HELP_URL || undefined;
// Latest releases shown; the file keeps the full history.
const SHOWN_RELEASES = 3;

type VersionInfo = { version: string; commit: string; builtAt: string | null };

/** The latest changelog entries, with a dot until this build's notes have been opened. */
export function WhatsNewCard() {
  const { unseen, markSeen } = useWhatsNewDot();
  const [open, setOpen] = useState(false);
  const [changelog, setChangelog] = useState<Changelog | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || changelog) return;
    // A plain fetch, not `api`: it is a static file next to the app, not an API route.
    fetch("/changelog.json")
      .then((r) => (r.ok ? (r.json() as Promise<Changelog>) : Promise.reject(new Error(String(r.status)))))
      .then(setChangelog)
      .catch(() => setFailed(true));
  }, [open, changelog]);

  const show = () => {
    setOpen(true);
    markSeen();
  };

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="flag" size={15} />
          What's new
          {unseen && <span className="whats-new-dot" role="status" aria-label="New since you last looked" />}
        </h2>
        <Button variant="secondary" onClick={show}>
          See what's new
        </Button>
      </div>
      <p className="profile__muted">Recent improvements to Astrum Connect.</p>

      <Modal open={open} onClose={() => setOpen(false)} title="What's new">
        <div className="whats-new">
          {failed && <p className="profile__muted">Could not load the release notes right now.</p>}
          {!failed && !changelog && <SkeletonText lines={5} />}
          {changelog?.releases.slice(0, SHOWN_RELEASES).map((release) => (
            <section key={release.title} className="whats-new__release">
              <h3>
                {release.title}
                {release.date && <span className="profile__muted"> · {release.date}</span>}
              </h3>
              {release.sections.map((section) => (
                <div key={section.heading}>
                  <h4>{section.heading}</h4>
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
        </div>
      </Modal>
    </section>
  );
}

/** App version, server version and commit, and a way to get help. */
export function AboutCard() {
  const [server, setServer] = useState<VersionInfo | null>(null);

  useEffect(() => {
    api.get<VersionInfo>("/version").then(setServer).catch(() => setServer(null));
  }, []);

  const unknown = (v?: string) => (v && v !== "unknown" ? v : "not available");

  return (
    <section className="profile__card">
      <div className="profile__card-head">
        <h2>
          <Icon name="info" size={15} />
          About
        </h2>
      </div>
      <dl className="about-list">
        <dt>App version</dt>
        <dd>{APP_VERSION}</dd>
        <dt>Server version</dt>
        <dd>{unknown(server?.version)}</dd>
        <dt>Server build</dt>
        <dd>{server?.commit && server.commit !== "unknown" ? server.commit.slice(0, 8) : "not available"}</dd>
      </dl>
      {HELP_URL && (
        <p className="profile__muted">
          <a href={HELP_URL} target="_blank" rel="noreferrer noopener">
            Get help
          </a>
        </p>
      )}
    </section>
  );
}
